import React, { useMemo } from 'react'
import { useTracerStudyStats, useTracerTrends } from '../../hooks/useAnalytics'
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, LineChart, Line
} from 'recharts'
import { Briefcase, DollarSign, Target, Loader2, AlertTriangle, Users } from 'lucide-react'

interface Props {
  department: string | null
  program: string | null
  batchYear: number | null
}

const STATUS_LABELS: Record<string, string> = {
  employed: 'Employed',
  unemployed: 'Unemployed',
  'self-employed': 'Self-Employed',
  studying: 'Studying',
}

const STATUS_COLORS: Record<string, string> = {
  employed: '#22c55e',
  unemployed: '#ef4444',
  'self-employed': '#3b82f6',
  studying: '#a855f7',
  other: '#6b7280',
}

const SALARY_LABELS: Record<string, string> = {
  under_20k: 'Under ₱20k',
  '20k_40k': '₱20k-40k',
  '40k_60k': '₱40k-60k',
  '60k_80k': '₱60k-80k',
  '80k_100k': '₱80k-100k',
  over_100k: 'Over ₱100k',
}

const SALARY_ORDER = ['under_20k', '20k_40k', '40k_60k', '60k_80k', '80k_100k', 'over_100k']

const RELEVANCE_LABELS: Record<string, string> = {
  highly_relevant: 'Highly Relevant',
  relevant: 'Relevant',
  somewhat_relevant: 'Somewhat Relevant',
  not_relevant: 'Not Relevant',
}

const RELEVANCE_COLORS: Record<string, string> = {
  highly_relevant: '#22c55e',
  relevant: '#3b82f6',
  somewhat_relevant: '#eab308',
  not_relevant: '#ef4444',
}

const EMPLOYER_LABELS: Record<string, string> = {
  private: 'Private',
  public: 'Public',
  'self-employed': 'Self-Employed',
  'non-profit': 'Non-Profit',
  government: 'Government',
}

const EMPLOYER_COLORS = ['#1e3a5f', '#6366f1', '#0891b2', '#059669', '#d97706']

const EMPLOYMENT_LENGTH_LABELS: Record<string, string> = {
  less_than_1_year: 'Less than 1 year',
  under_1_year: 'Less than 1 year',
  less_than_1: 'Less than 1 year',
  '1_3_years': '1–3 years',
  '3_5_years': '3–5 years',
  '5_10_years': '5–10 years',
  over_10_years: 'Over 10 years',
  over_5_years: 'Over 5 years',
}
const EMPLOYMENT_LENGTH_ORDER = [
  'less_than_1_year', 'under_1_year', 'less_than_1',
  '1_3_years', '3_5_years', '5_10_years', 'over_5_years', 'over_10_years',
]

const TracerStudyTab: React.FC<Props> = ({ department, program, batchYear }) => {
  const { data, loading, error } = useTracerStudyStats(department, program, batchYear)
  const { data: trends } = useTracerTrends(department)

  // ── Derived chart data ──────────────────────────────────
  const employmentPieData = useMemo(() => {
    if (!data?.employment_by_status) return []
    return Object.entries(data.employment_by_status).map(([key, val]) => ({
      name: STATUS_LABELS[key] || key,
      value: val,
      color: STATUS_COLORS[key] || '#6b7280',
    }))
  }, [data])

  const salaryBarData = useMemo(() => {
    if (!data?.salary_distribution) return []
    return SALARY_ORDER
      .filter(k => data.salary_distribution[k] != null)
      .map(k => ({
        name: SALARY_LABELS[k] || k,
        count: data.salary_distribution[k] || 0,
      }))
  }, [data])

  const relevanceBarData = useMemo(() => {
    if (!data?.job_relevance_distribution) return []
    return Object.entries(data.job_relevance_distribution).map(([key, val]) => ({
      name: RELEVANCE_LABELS[key] || key,
      count: val,
      color: RELEVANCE_COLORS[key] || '#6b7280',
    }))
  }, [data])

  const employerDonutData = useMemo(() => {
    if (!data?.employer_type_distribution) return []
    return Object.entries(data.employer_type_distribution).map(([key, val]) => ({
      name: EMPLOYER_LABELS[key] || key,
      value: val,
    }))
  }, [data])

  const fieldsBarData = useMemo(() => {
    if (!data?.top_fields_of_work) return []
    return data.top_fields_of_work.map(f => ({ name: f.field, count: f.count }))
  }, [data])

  const skillsBarData = useMemo(() => {
    if (!data?.top_skills) return []
    return data.top_skills.map(s => ({ name: s.skill, count: s.count }))
  }, [data])

  const employmentLengthBarData = useMemo(() => {
    if (!data?.employment_length_distribution) return []
    // Deduplicate labels (some keys are aliases for the same bucket)
    const seen = new Set<string>()
    return EMPLOYMENT_LENGTH_ORDER
      .filter(k => data.employment_length_distribution[k] != null && data.employment_length_distribution[k] > 0)
      .reduce<{ name: string; count: number }[]>((acc, k) => {
        const label = EMPLOYMENT_LENGTH_LABELS[k] || k
        if (!seen.has(label)) {
          seen.add(label)
          acc.push({ name: label, count: data.employment_length_distribution[k] })
        }
        return acc
      }, [])
  }, [data])

  const trendLineData = useMemo(() => {
    if (!trends) return []
    return trends.map(t => ({
      year: t.year_graduated,
      'Employment Rate': t.employment_rate != null ? Math.min(100, Math.max(0, t.employment_rate)) : null,
      'Avg Outcome Score': t.avg_peo_score ?? null,
    }))
  }, [trends])

  // Job relevance rate
  const jobRelevanceRate = useMemo(() => {
    if (!data?.job_relevance_distribution) return 0
    const dist = data.job_relevance_distribution
    const relevant = (dist['highly_relevant'] || 0) + (dist['relevant'] || 0)
    const total = Object.values(dist).reduce((a, b) => a + b, 0)
    return total > 0 ? Math.round(relevant / total * 100) : 0
  }, [data])

  // Average salary band
  const avgSalaryLabel = useMemo(() => {
    if (!data?.salary_distribution) return 'N/A'
    const dist = data.salary_distribution
    const total = Object.values(dist).reduce((a, b) => a + b, 0)
    if (total === 0) return 'N/A'
    // Find the mode (most common salary band)
    let maxKey = ''
    let maxVal = 0
    Object.entries(dist).forEach(([k, v]) => {
      if (v > maxVal) { maxKey = k; maxVal = v }
    })
    return SALARY_LABELS[maxKey] || maxKey
  }, [data])

  // ── Loading / Error / Empty ──────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-blue-900" />
        <span className="ml-2 text-sm text-gray-500">Loading tracer study data...</span>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5" />
        <div>
          <p className="text-sm font-semibold text-red-800">Failed to load tracer study data</p>
          <p className="text-xs text-red-600 mt-0.5">{error}</p>
        </div>
      </div>
    )
  }

  if (!data || data.total_alumni === 0) {
    const scope = program
      ? `program ${program}`
      : department
        ? `the ${department}`
        : 'any department'
    const batchSuffix = batchYear ? ` (Class of ${batchYear})` : ''
    return (
      <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
        <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
        <h3 className="text-base font-bold text-gray-700 mb-1">No Alumni Found</h3>
        <p className="text-sm text-gray-500">
          No approved alumni records match <span className="font-medium">{scope}</span>{batchSuffix}.
        </p>
        <p className="text-xs text-gray-400 mt-1.5">Try broadening the filters or check that alumni in this selection have been approved.</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
            <Briefcase className="w-4 h-4 text-green-600" />
            Employment Rate
          </div>
          <p className="text-2xl font-bold text-gray-900">{data.employment_rate}%</p>
          <p className="text-xs text-gray-400 mt-0.5">{data.total_employed} of {data.total_alumni} alumni</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
            <DollarSign className="w-4 h-4 text-emerald-600" />
            Most Common Salary
          </div>
          <p className="text-2xl font-bold text-gray-900">{avgSalaryLabel}</p>
          <p className="text-xs text-gray-400 mt-0.5">among employed alumni</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
            <Target className="w-4 h-4 text-blue-600" />
            Job Relevance
          </div>
          <p className="text-2xl font-bold text-gray-900">{jobRelevanceRate}%</p>
          <p className="text-xs text-gray-400 mt-0.5">relevant or highly relevant</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
            <Users className="w-4 h-4 text-indigo-600" />
            Total Alumni
          </div>
          <p className="text-2xl font-bold text-gray-900">{data.total_alumni}</p>
          <p className="text-xs text-gray-400 mt-0.5">approved & tracked</p>
        </div>
      </div>

      {/* Row 1: Employment Pie + Salary Bar */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Employment Status Pie */}
        {employmentPieData.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h3 className="text-sm font-bold text-gray-700 mb-4">Employment Status Distribution</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={employmentPieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  dataKey="value"
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  labelLine={{ strokeWidth: 1 }}
                >
                  {employmentPieData.map((entry, i) => (
                    <Cell key={i} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Salary Distribution Bar */}
        {salaryBarData.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h3 className="text-sm font-bold text-gray-700 mb-4">Salary Distribution</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={salaryBarData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="count" fill="#1e3a5f" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Row 2: Job Relevance + Employer Type */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Job Relevance */}
        {relevanceBarData.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h3 className="text-sm font-bold text-gray-700 mb-4">Job Relevance Distribution</h3>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={relevanceBarData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={120} />
                <Tooltip />
                <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                  {relevanceBarData.map((entry, i) => (
                    <Cell key={i} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Employer Type Donut */}
        {employerDonutData.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h3 className="text-sm font-bold text-gray-700 mb-4">Employer Type Distribution</h3>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={employerDonutData}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={85}
                  dataKey="value"
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  labelLine={{ strokeWidth: 1 }}
                >
                  {employerDonutData.map((_, i) => (
                    <Cell key={i} fill={EMPLOYER_COLORS[i % EMPLOYER_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Row 3: Top Fields + Top Skills */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Top Fields of Work */}
        {fieldsBarData.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h3 className="text-sm font-bold text-gray-700 mb-4">Top Fields of Work</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={fieldsBarData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={150} />
                <Tooltip />
                <Bar dataKey="count" fill="#6366f1" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Top Skills */}
        {skillsBarData.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h3 className="text-sm font-bold text-gray-700 mb-4">Top Skills Acquired</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={skillsBarData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={150} />
                <Tooltip />
                <Bar dataKey="count" fill="#0891b2" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Row 3b: Employment Length Distribution */}
      {employmentLengthBarData.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="text-sm font-bold text-gray-700 mb-1">Employment Length Distribution</h3>
          <p className="text-xs text-gray-400 mb-4">How long employed alumni have been in their current job — indicates workforce stability and retention.</p>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={employmentLengthBarData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="count" fill="#059669" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Row 4: Employment Trend Line */}
      {trendLineData.length > 1 && (
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="text-sm font-bold text-gray-700 mb-4">Employment Rate Trend by Graduation Year</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={trendLineData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="year" tick={{ fontSize: 11 }} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line type="monotone" dataKey="Employment Rate" stroke="#22c55e" strokeWidth={2} dot={{ r: 4 }} connectNulls={true} />
              <Line type="monotone" dataKey="Avg Outcome Score" stroke="#6366f1" strokeWidth={2} dot={{ r: 4 }} connectNulls={true} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Unemployment Reasons */}
      {data.unemployment_reasons && data.unemployment_reasons.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="text-sm font-bold text-gray-700 mb-4">Unemployment Reasons</h3>
          <div className="space-y-2">
            {data.unemployment_reasons.map((r, i) => (
              <div key={i} className="flex items-center justify-between bg-red-50 rounded-lg px-4 py-2.5">
                <span className="text-sm text-red-800">{r.reason}</span>
                <span className="text-sm font-bold text-red-600">{r.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default TracerStudyTab
